import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Box, Card, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { clearProject } from '../redux/slices/project';
import { getAccount, getAccountProjects, getAccountGroups, updateAccount } from '../redux/ducks/account';
import { showAlert } from '../redux/slices/alerts';
import AccountInfoGridBox from '../components/AccountInfoGridBox';
import DeactivatedAlert from '../components/DeactivatedAlert';
import RetainerOverageNotice from '../components/RetainerOverageNotice';
import InfoSmallBox from '../components/InfoSmallBox';
import AccountWideInfoBox from '../components/AccountWideInfoBox';
import AccountHoursByGroupDialog from '../components/AccountHoursByGroupDialog';
import { getCurrentMonth, getLastMonth } from '../utils/dateUtils';
import AccountTabs from '../components/AccountTabs';
import PageHeader from '../components/PageHeader';
import { convertMinutesToHoursSorted } from '../utils/timeUtils';
import { useHasPermission } from '../utils/permissionUtils';

const ContainerBox = styled(Box)(({ theme }) => ({
    width: '100%',
    minWidth: 0,
    maxWidth: theme.breakpoints.values.lg,
    marginRight: 'auto',
    marginLeft: 'auto',
    padding: theme.spacing(2),
    overflowX: 'clip',
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(3)
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(4)
    }
}))

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
}));

const Account = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const { accountSlug, tab } = useParams();
    const [accountData, setAccountData] = useState({ client: {}, billingTerms: {}, accountManager: {} });
    const [accountGroups, setAccountGroups] = useState([]);
    const [accountProjects, setAccountProjects] = useState([])
    const tabStatus = tab === 'completedProjects' ? 'completed' : 'open';
    const [status, setStatus] = useState(tabStatus);
    const [loading, setLoading] = useState(true);
    const refreshGroupsTrigger = useSelector((state) => state.refreshTriggers.groups);
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth.currentUser);
    const [renameOpen, setRenameOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [groupHoursOpen, setGroupHoursOpen] = useState(false);
    const [groupHoursMonth, setGroupHoursMonth] = useState(getCurrentMonth());

    const isOwnAccount = Boolean(accountData?.client?._id) && accountData.client._id === currentUser?.client?._id;
    const canRenameAccount = hasPermission('editAccount') || (hasPermission('manageOwnAccounts') && isOwnAccount);

    const handleOpenRename = () => {
        setNewName(accountData?.name || '');
        setRenameOpen(true);
    };

    const handleRename = () => {
        const trimmed = newName.trim();
        if (!trimmed || trimmed === accountData?.name) {
            setRenameOpen(false);
            return;
        }
        setRenaming(true);
        dispatch(updateAccount(accountData._id, { name: trimmed }, (response) => {
            setRenaming(false);
            if (!response?.account) return;
            setAccountData((prev) => ({ ...prev, ...response.account, client: prev.client }));
            setRenameOpen(false);
            dispatch(showAlert({ severity: 'success', message: 'Account renamed.' }));
            if (response.account.slug && response.account.slug !== accountSlug) {
                navigate(`/account/${response.account.slug}${tab ? `/${tab}` : ''}`, { replace: true });
            }
        }));
    };

    useEffect(() => {
        const handleAccountData = (data) => {
            setAccountData(data);
            dispatch(clearProject());
        };

        dispatch(getAccount(accountSlug, handleAccountData));
    }, [accountSlug, dispatch]);

    // Switching status tabs fires a fresh read before the previous one lands, so the response that
    // wins has to be the one that was asked for last, not the one that happens to arrive last.
    const projectsRequestRef = useRef(0);
    const fetchProjects = useCallback((status) => {
        if (!accountData?._id) return;
        setStatus(status);
        setLoading(true);
        const requestId = ++projectsRequestRef.current;
        dispatch(getAccountProjects(accountData._id, status, (data) => {
            if (projectsRequestRef.current !== requestId) return;
            setAccountProjects(data || []);
            setLoading(false);
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData?._id, dispatch]);

    useEffect(() => {
        if (!accountData?._id) return;
        fetchProjects(tabStatus);
        dispatch(getAccountGroups(accountData._id, (data) => setAccountGroups(data || [])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData._id, tabStatus, dispatch, fetchProjects]);

    useEffect(() => {
        if (refreshGroupsTrigger && accountData._id !== undefined) {
            dispatch(getAccountGroups(accountData._id, (data) => setAccountGroups(data || [])));
        }
    }, [refreshGroupsTrigger, dispatch, accountData._id]);

    const byGroupAction = (month) => {
        if (!Array.isArray(accountData.groupsTime) || accountData.groupsTime.length === 0) return null;

        return (
            <Tooltip title="View these hours broken down by group">
                <IconButton
                    size="small"
                    aria-label="View hours by group"
                    onClick={(event) => {
                        event.stopPropagation();
                        setGroupHoursMonth(month);
                        setGroupHoursOpen(true);
                    }}
                >
                    <BarChartIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        );
    };

    let lastMonthTime = {}
    let thisMonthTime = {}

    if (accountData.aggregatedTime !== undefined) {
        lastMonthTime = {
            title: 'Previous Month',
            content: convertMinutesToHoursSorted(accountData.aggregatedTime.monthly[getLastMonth()] || {Hourly: 0}),
            headerAction: byGroupAction(getLastMonth())
        }

        thisMonthTime = {
            title: 'Current Month',
            content: convertMinutesToHoursSorted(accountData.aggregatedTime.monthly[getCurrentMonth()] || {Hourly: 0}),
            headerAction: byGroupAction(getCurrentMonth())
        }
    }

    const accountInactive = accountData?.active === false;
    const clientInactive = accountData?.client?.active === false;

    return (
        <ContainerBox aria-label="ContainerBox">
            <PageHeader
                title={accountData?.name || "Account"}
                titleActions={canRenameAccount && accountData?._id ? (
                    <Tooltip title="Rename this account">
                        <IconButton size="small" onClick={handleOpenRename}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null}
            />
            {clientInactive && (
                <DeactivatedAlert entityType="account" parentInactive parentName={accountData?.client?.name} />
            )}
            {accountInactive && !clientInactive && (
                <DeactivatedAlert entityType="account" />
            )}
            <RetainerOverageNotice status={accountData?.retainerStatus} />
            <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
                <Grid item xs={12} md={6}>
                    <AccountInfoGridBox accountData={accountData} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Grid container spacing={{ xs: 3, md: 4, lg: 5 }} item xs={12} md={6}>
                        <InfoSmallBox {...lastMonthTime} />
                        <InfoSmallBox {...thisMonthTime} />
                        {(status === 'open' || canSeeInternal) &&
                            <AccountWideInfoBox accountData={accountData} accountProjects={accountProjects} loading={loading} status={status} />
                        }
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <StyledCard p={{ xs: 3, md: 4, lg: 5 }}>
                        <AccountTabs accountId={accountData._id} accountData={accountData} accountProjects={accountProjects} accountGroups={accountGroups} fetchProjects={fetchProjects} loading={loading} />
                    </StyledCard>
                </Grid>

            </Grid >

            <AccountHoursByGroupDialog
                open={groupHoursOpen}
                onClose={() => setGroupHoursOpen(false)}
                groupsTime={accountData.groupsTime}
                month={groupHoursMonth}
                onMonthChange={setGroupHoursMonth}
            />

            <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Rename this account</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The account's web address is built from its name, so renaming it will change the link to this page. Any links you shared earlier will stop working.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="normal"
                        label="Account name"
                        value={newName}
                        onChange={(event) => setNewName(event.target.value)}
                        disabled={renaming}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameOpen(false)} disabled={renaming}>Cancel</Button>
                    <Button onClick={handleRename} variant="contained" disabled={renaming || !newName.trim()}>
                        {renaming ? 'Saving…' : 'Rename account'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ContainerBox >
    );
};

export default Account;
